import type { ContinuationTarget } from "~/helpers/pendingIntentLifecycle";
import type { ReceiverIdentityAdapter } from "~produce/production/helpers/continuationAdapter";

import { resolveNavSlotPath } from "~/components/mobile/bottomNav/mobileBottomNav";
import AVAILABILITY_NAMESPACES from "~mobile/availability/availabilityNamespaces";

export const AVAILABILITY_HISTORY_TARGET_KIND = "availabilityHistory";

export const AVAILABILITY_HISTORY_RAIL_ID = "history";

export interface AvailabilityHistoryScope {
	basePath: string;
	itemSearch?: string;
	locationId?: string;
}

export const buildAvailabilityHistoryTarget = (scope: AvailabilityHistoryScope): ContinuationTarget => ({
	targetId: JSON.stringify(scope),
	targetKind: AVAILABILITY_HISTORY_TARGET_KIND
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null && !Array.isArray(value);

const isOptionalFilledString = (value: unknown): value is string | undefined =>
	value === undefined || (typeof value === "string" && value !== "");

const isAvailabilityNamespace = (value: unknown): value is string =>
	AVAILABILITY_NAMESPACES.some((namespace) => namespace === value);

const parseTargetId = (targetId: string): unknown => {
	try {
		return JSON.parse(targetId);
	} catch {
		return null;
	}
};

export const readAvailabilityHistoryScope = (
	target: ContinuationTarget | null | undefined
): AvailabilityHistoryScope | null => {
	if (target?.targetKind !== AVAILABILITY_HISTORY_TARGET_KIND) return null;
	const scope = parseTargetId(target.targetId);
	if (!isRecord(scope) || !isAvailabilityNamespace(scope.basePath)) return null;
	if (!isOptionalFilledString(scope.locationId) || !isOptionalFilledString(scope.itemSearch)) return null;
	return { basePath: scope.basePath, itemSearch: scope.itemSearch, locationId: scope.locationId };
};

const availabilityHistoryReceiverIdentityAdapter: ReceiverIdentityAdapter = {
	buildCleanPath: (target) => {
		const scope = readAvailabilityHistoryScope(target);
		return scope ? resolveNavSlotPath(scope.basePath, AVAILABILITY_HISTORY_RAIL_ID) : "";
	},
	matchesRoute: () => null
};

export default availabilityHistoryReceiverIdentityAdapter;
